import { Component, computed, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  AppointmentDocumentDto,
  AppointmentsClient,
  BookAppointmentCommand,
  CalendarSlotDto,
  FileType,
  IntervalDto,
} from '../../model/fss.api';
import { LoaderService } from '../loader/loader.service';
import { FileService } from '../../services/file.service';
import { LocationCompanyService } from '../../services/location-company.service';
import { LocationIndividualService } from '../../services/location-individual.service';
import { UserService } from '../../services/user.service';
import { AlertService } from '../../services/alert.service';
import { DropdownComponent, DropdownOptions } from '../../ui/dropdown/dropdown';
import { CalendarComponent } from '../../ui/calendar/calendar';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../ui/button/button';
import { Hourglas03 } from '../../icons/hourglas-03/hourglas-03';
import { XClose } from '../../icons/x-close/x-close';
import { InfoCircle } from '../../icons/info-circle/info-circle';
import { LinkExternal02 } from '../../icons/link-external-02/link-external-02';
import { Attachment02 } from '../../icons/attachment-02/attachment-02';
import { CheckCircle } from '../../icons/check-circle/check-circle';
import { Pdf } from '../../icons/pdf/pdf';
import { Dwg } from '../../icons/dwg/dwg';
import { Dialog } from 'primeng/dialog';
import { slotCalendarDays } from '../../functions/slot-calendar';

type FileKind = 'pdf' | 'autoCad';

@Component({
  standalone: true,
  selector: 'app-client-appointment-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DropdownComponent,
    CalendarComponent,
    ButtonComponent,
    XClose,
    InfoCircle,
    LinkExternal02,
    Attachment02,
    CheckCircle,
    Pdf,
    Dwg,
    Dialog,
    Hourglas03,
  ],
  templateUrl: './client-appointment-form.html',
  styleUrl: './client-appointment-form.scss',
})
export class ClientAppointmentForm {
  private readonly _fb = inject(FormBuilder);
  private readonly _loader = inject(LoaderService);
  private readonly _alert = inject(AlertService);
  private readonly _appointments = inject(AppointmentsClient);
  private readonly _files = inject(FileService);
  readonly user = inject(UserService);
  readonly companyLocation = inject(LocationCompanyService);
  readonly individualLocation = inject(LocationIndividualService);

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;

  packageHours = signal<number | null>(null);
  durationOptions = signal<DropdownOptions[]>([]);
  calendarSlots = signal<CalendarSlotDto[]>([]);
  availableIntervals = signal<IntervalDto[]>([]);
  slotsLoading = signal<boolean>(false);

  noSlots = computed(
    () => !this.slotsLoading() && this.calendarSlots().length === 0,
  );

  pdfFile = signal<File | null>(null);
  autoCadFile = signal<File | null>(null);

  private readonly _calendarDays = computed(() => slotCalendarDays(this.calendarSlots()));
  minDate = computed(() => this._calendarDays().minDate);
  maxDate = computed(() => this._calendarDays().maxDate);
  disabledDates = computed(() => this._calendarDays().disabledDates);

  /** Confirm dialog */
  showConfirmDialog = signal(false);

  form: FormGroup = this._fb.group({
    duration: new FormControl<number | null>(null, { validators: [Validators.required] }),
    date: new FormControl<Date | null>(null, { validators: [Validators.required] }),
    timeSlot: new FormControl<IntervalDto | null>(null, { validators: [Validators.required] }),
  });

  constructor() {
    this._loadBookingData();

    this.form.controls['duration']!.valueChanges.subscribe((duration) => {
      if (duration != null) {
        this._loadCalendarSlots(duration);
      }
    });

    this.form.controls['date']!.valueChanges.subscribe((date: Date | null) => {
      this.form.controls['timeSlot']!.setValue(null, { emitEvent: false });
      if (!date) {
        this.availableIntervals.set([]);
        return;
      }
      const key = this._dateKey(date);
      const slot = this.calendarSlots().find((s) => this._dateKey(s.dateTime!) === key);
      this.availableIntervals.set(slot?.intervals ?? []);
    });
  }

  get durationCtrl(): FormControl {
    return this.form.controls['duration'] as FormControl;
  }

  get dateCtrl(): FormControl {
    return this.form.controls['date'] as FormControl;
  }

  isIntervalSelected(interval: IntervalDto): boolean {
    const selected = this.form.controls['timeSlot']!.value as IntervalDto | null;
    return !!selected && selected.from === interval.from && selected.to === interval.to;
  }

  selectInterval(interval: IntervalDto): void {
    this.form.controls['timeSlot']!.setValue(interval);
  }

  formatTime(value: string | undefined): string {
    if (!value) return '';
    const match = value.match(/(\d{1,2}):(\d{2})/);
    if (!match) return value;
    return `${match[1]!.padStart(2, '0')}:${match[2]}`;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent, kind: FileKind): void {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files?.[0];
    if (file) this._setFile(kind, file);
  }

  onFileSelected(event: Event, kind: FileKind): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this._setFile(kind, file);
    input.value = '';
  }

  removeFile(kind: FileKind, event: Event): void {
    event.stopPropagation();
    if (kind === 'pdf') this.pdfFile.set(null);
    else this.autoCadFile.set(null);
  }

  cancel(): void {
    this._goBackToHome();
  }

  bookAnother(): void {
    this.form.reset();
    this.pdfFile.set(null);
    this.autoCadFile.set(null);
    this.availableIntervals.set([]);
    const firstDuration = this.durationOptions()[0]?.value ?? null;
    if (firstDuration != null) this.durationCtrl.setValue(firstDuration);
    this.showConfirmDialog.set(false);
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const date = this.form.controls['date']!.value as Date;
    const interval = this.form.controls['timeSlot']!.value as IntervalDto;
    const duration = this.form.controls['duration']!.value as number;
    const dateTime = this._combineDateAndTime(date, interval.from!);

    this._loader.setShowLoader = true;

    const pdf = this.pdfFile();
    const autoCad = this.autoCadFile();

    if (pdf || autoCad) {
      this._loader.setLoadingText = 'Otpremanje fajlova...';
    }

    let pdfDoc: AppointmentDocumentDto | undefined;
    let autoCadDoc: AppointmentDocumentDto | undefined;
    try {
      pdfDoc = pdf ? await this._uploadDocument(pdf, FileType.PDF) : undefined;
      autoCadDoc = autoCad ? await this._uploadDocument(autoCad, FileType.DWG) : undefined;
    } catch {
      // The presigned PUT goes straight to storage, so the global error interceptor doesn't
      // show anything for it. Nothing is booked; the form stays filled in for another try.
      this._loader.reset();
      this._alert.addFailedMsg('Otpremanje fajla nije uspelo. Pokušajte ponovo.');
      return;
    }

    const command = new BookAppointmentCommand({
      userId: this.user.getUserData.id,
      duration,
      dateTime,
      pdf: pdfDoc,
      autoCad: autoCadDoc,
    });

    this._loader.setLoadingText = 'Zakazivanje termina...';

    await firstValueFrom(this._appointments.bookAppointment(command));
    this.showConfirmDialog.set(true);
  }

  private async _uploadDocument(file: File, type: FileType): Promise<AppointmentDocumentDto> {
    const res = await firstValueFrom(this._appointments.getAppointmentDocumentUploadUrl(type));
    const { storageFileName, uploadUrl, contentType } = res.result!;
    await this._files.uploadFileToS3(uploadUrl!, file, contentType!);
    return new AppointmentDocumentDto({
      name: file.name,
      storageFileName: storageFileName!,
      type,
      size: file.size,
    });
  }

  private _setFile(kind: FileKind, file: File): void {
    if (kind === 'pdf') this.pdfFile.set(file);
    else this.autoCadFile.set(file);
  }

  private _goBackToHome(): void {
    if (this.user.isCompany()) {
      this.companyLocation.routeToHomePage();
    } else {
      this.individualLocation.routeToHomePage();
    }
  }

  private _combineDateAndTime(date: Date, time: string): Date {
    const match = time.match(/(\d{1,2}):(\d{2})/);
    const hours = match ? Number(match[1]) : 0;
    const minutes = match ? Number(match[2]) : 0;
    const result = new Date(date);
    result.setHours(hours, minutes, 0, 0);
    return result;
  }

  private async _loadBookingData(): Promise<void> {
    const userId = this.user.getUserData.id;
    const res = await firstValueFrom(this._appointments.getDataForBooking(userId));
    const data = res.result;
    this.packageHours.set(data?.packageHours ?? 0);

    const options: DropdownOptions[] = (data?.durations ?? []).map((d) => ({
      label: `${d}h`,
      value: d,
    }));
    this.durationOptions.set(options);

    const firstDuration = options[0]?.value ?? null;
    this.durationCtrl.setValue(firstDuration);
  }

  private async _loadCalendarSlots(duration: number): Promise<void> {
    this.form.controls['date']!.setValue(null, { emitEvent: false });
    this.form.controls['timeSlot']!.setValue(null, { emitEvent: false });
    this.availableIntervals.set([]);
    this.slotsLoading.set(true);

    const userId = this.user.getUserData.id;
    try {
      const res = await firstValueFrom(this._appointments.getCalendarSlots(userId, duration));
      const slots = res.result?.slots ?? [];
      this.calendarSlots.set(slots);
    } finally {
      this.slotsLoading.set(false);
    }
  }

  private _dateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }
}
